/**
 * Shell de layout para las rutas autenticadas.
 *
 * Contiene la barra lateral con navegación + header superior con el
 * usuario + botón de sesión. El contenido específico de cada página se
 * renderiza en el <Outlet /> central.
 *
 * Responsive (Sprint 7.5):
 * - Desktop (>=768px): sidebar fijo de 240px siempre visible.
 * - Tablet/mobile (<768px): sidebar colapsa en drawer lateral con backdrop.
 *   Hamburger button en el header superior abre el drawer. El drawer se
 *   cierra automáticamente al navegar a otra ruta y al hacer click afuera.
 * - Header: min-w fluido en el selector de municipio (no más rígido
 *   min-w-[220px] que rompía viewports < 375px).
 *
 * A11y:
 * - Drawer con role="dialog" + aria-modal + aria-labelledby cuando abierto.
 * - Focus trap informal: el hamburger recibe focus al cerrar.
 * - Esc cierra el drawer.
 * - Backdrop con aria-hidden + click handler.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { AssistantQuotaBadge } from "@/components/AssistantQuotaBadge";

import { ApiError, fetchBpinPendingSummary } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

interface NavItem {
  to: string;
  label: string;
  /** Visible pero no navegable (feature futura). */
  disabled?: boolean;
  /** Match prefijo vs. match exacto. */
  end?: boolean;
}

function buildNavItems(activeDane: string | null): NavItem[] {
  return [
    { to: "/radar", label: "Radar", end: true },
    {
      // Si aún no hay muni activo mandamos a onboarding (defensivo —
      // OnboardingGate ya debería haberlo redirigido).
      to: activeDane ? `/municipio/${activeDane}` : "/onboarding",
      label: "Ficha municipal",
    },
    { to: "/expedientes", label: "Expedientes" },
    { to: "/alertas", label: "Alertas" },
    { to: "/oportunidades-perdidas", label: "Oportunidades perdidas" },
    { to: "/membresia", label: "Membresía" },
  ];
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { daneCode, name, displayName, isStaff } = useMunicipality();
  const navItems = buildNavItems(daneCode);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Devuelve el focus al hamburger tras cerrar (a11y).
    hamburgerRef.current?.focus();
  }, []);

  // Cerrar drawer al cambiar de ruta. Se ajusta durante el render en vez de
  // en un efecto: el efecto dejaba pintar un frame con el drawer todavía
  // abierto sobre la ruta nueva. Patrón documentado por React para "resetear
  // estado cuando cambia una prop".
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    setDrawerOpen(false);
  }

  // Cerrar drawer con Esc.
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [drawerOpen, closeDrawer]);

  // Bloquear scroll del body cuando el drawer está abierto (mobile).
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Sidebar desktop fijo */}
      <Sidebar
        currentPath={location.pathname}
        items={navItems}
        daneCode={daneCode}
        className="hidden md:flex"
      />

      {/* Drawer mobile + backdrop */}
      {drawerOpen && (
        <>
          <div
            aria-hidden="true"
            onClick={closeDrawer}
            className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-title"
            className="fixed inset-y-0 left-0 z-50 flex w-60 max-w-[80vw] md:hidden"
          >
            <Sidebar
              currentPath={location.pathname}
              items={navItems}
              daneCode={daneCode}
              className="flex w-full shadow-2xl"
              onItemClick={closeDrawer}
              closeButton={
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Cerrar navegación"
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                >
                  <CloseIcon className="h-5 w-5" />
                </button>
              }
            />
          </div>
        </>
      )}

      <div className="flex flex-1 flex-col">
        <TopHeader
          userEmail={user?.email ?? null}
          onSignOut={signOut}
          municipalityName={name}
          municipalityDane={daneCode}
          tenantDisplayName={displayName}
          onOpenDrawer={() => setDrawerOpen(true)}
          hamburgerRef={hamburgerRef}
          isStaff={isStaff}
        />
        <BpinPendingBanner />
        <main className="flex-1 px-4 py-4 sm:px-6 sm:py-6">
          {/* Soporta dos modos de uso: wrapper (children) o parent route (Outlet).
              App.tsx lo usa como parent route; tests directos pueden pasar children. */}
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

function Sidebar({
  currentPath,
  items,
  daneCode,
  className = "",
  onItemClick,
  closeButton,
}: {
  currentPath: string;
  items: NavItem[];
  /** DANE del muni activo para el badge de cuota IA. */
  daneCode: string | null;
  className?: string;
  /** Callback para cerrar el drawer al hacer click en un item (solo mobile). */
  onItemClick?: () => void;
  /** Botón X en el header del drawer (solo mobile). */
  closeButton?: ReactNode;
}) {
  return (
    <aside
      aria-label="Navegación principal"
      className={"w-60 shrink-0 flex-col border-r border-slate-200 bg-white px-4 py-6 " + className}
    >
      <div className="mb-8 flex items-start justify-between px-2">
        <div>
          <p id="drawer-title" className="text-lg font-extrabold tracking-tight text-brand-700">
            kaptar
          </p>
          <p className="text-[11px] font-medium text-slate-500">
            La forma inteligente de captar recursos públicos
          </p>
        </div>
        {closeButton}
      </div>

      <nav className="flex flex-col gap-1">
        {items.map((item) => {
          if (item.disabled) {
            return (
              <span
                key={item.label}
                aria-disabled="true"
                className="flex cursor-not-allowed items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-slate-400"
              >
                {item.label}
                <span className="text-[10px] tracking-wide text-slate-300 uppercase">Pronto</span>
              </span>
            );
          }
          // Active state: match por prefijo salvo que `end` esté activo.
          const basePath = (item.to.split("?")[0] ?? item.to).replace(/\/$/, "");
          const isActive = item.end ? currentPath === item.to : currentPath.startsWith(basePath);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onItemClick}
              aria-current={isActive ? "page" : undefined}
              className={
                "rounded-md px-3 py-2 text-sm font-medium transition " +
                (isActive ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-100")
              }
            >
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <AssistantQuotaBadge daneCode={daneCode} />

      <p className="mt-auto pt-8 text-[10px] text-slate-400">v0.1 · Sprint 17.4</p>
    </aside>
  );
}

function TopHeader({
  userEmail,
  onSignOut,
  municipalityName,
  municipalityDane,
  tenantDisplayName,
  onOpenDrawer,
  hamburgerRef,
  isStaff = false,
}: {
  userEmail: string | null;
  onSignOut: () => void | Promise<void>;
  municipalityName: string | null;
  municipalityDane: string | null;
  tenantDisplayName: string | null;
  onOpenDrawer: () => void;
  hamburgerRef: React.Ref<HTMLButtonElement>;
  /** Si el user tiene `is_staff=true`, mostramos un link al panel admin. */
  isStaff?: boolean;
}) {
  return (
    <header
      aria-label="Barra superior"
      className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:gap-4 sm:px-6"
    >
      {/* Hamburger: solo mobile */}
      <button
        ref={hamburgerRef}
        type="button"
        onClick={onOpenDrawer}
        aria-label="Abrir navegación"
        className="inline-flex items-center justify-center rounded-md p-2 text-slate-700 hover:bg-slate-100 md:hidden"
      >
        <HamburgerIcon className="h-5 w-5" />
      </button>

      <div className="flex min-w-0 flex-1 flex-col sm:min-w-[220px] sm:flex-initial">
        <span className="text-[10px] tracking-wide text-slate-400 uppercase">Municipio activo</span>
        <span
          data-testid="active-municipality"
          className="truncate text-sm font-semibold text-slate-700"
        >
          {municipalityName && municipalityDane
            ? `${municipalityName} · ${municipalityDane}`
            : tenantDisplayName || "Selecciona un municipio"}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        {isStaff && (
          <NavLink
            to="/admin"
            data-testid="appshell-admin-link"
            className="rounded-md border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
            title="Ir al panel administrativo"
          >
            Panel admin
          </NavLink>
        )}
        {userEmail && (
          <span className="hidden text-xs text-slate-500 sm:inline">
            Conectado como <strong className="text-slate-700">{userEmail}</strong>
          </span>
        )}
        <NavLink
          to="/cuenta"
          data-testid="appshell-cuenta-link"
          className={({ isActive }) =>
            "rounded-md px-2 py-1.5 text-xs font-semibold hover:bg-slate-100 " +
            (isActive ? "text-brand-700" : "text-slate-700")
          }
        >
          Mi cuenta
        </NavLink>
        <button
          type="button"
          onClick={() => {
            void onSignOut();
          }}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Cerrar sesión
        </button>
      </div>
    </header>
  );
}

function HamburgerIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

/**
 * Banner global persistente cuando el tenant tiene proyectos radicados
 * (status >= submitted) sin BPIN registrado (Sprint 11 follow-up #3b).
 *
 * Por qué tan visible: sin BPIN, Kaptar nunca puede detectar el primer
 * giro en SIIF Nación → no factura comisión. Es un blocker silencioso
 * del modelo de negocio. El banner empuja al usuario a entrar al
 * expediente y registrar el BPIN.
 *
 * Sprint Blindaje — PERF-6 (julio 2026): el polling bajó de 60s a 10min.
 * Este query corre en TODA sesión autenticada (AppShell envuelve todas
 * las rutas con muni activo) — a 60s eran ~7 requests/min agregados por
 * usuario activo sin que el dato cambie con esa frecuencia (un BPIN se
 * registra una vez cada tanto, no cada minuto). Para que el banner siga
 * sintiéndose inmediato cuando SÍ cambia algo relevante (se registra un
 * BPIN o el status cruza el umbral submitted), `ExpedienteDetail`
 * invalida esta query explícitamente (`queryClient.invalidateQueries`)
 * en el `onChanged` de `BpinSection` y `StatusControls` — el refetch
 * automático de 10min queda como red de seguridad, no como mecanismo
 * principal de actualización.
 *
 * Si el endpoint falla (red, 401, etc.) NO mostramos el banner —
 * preferimos un falso negativo a un banner roto.
 */
function BpinPendingBanner() {
  const { daneCode } = useMunicipality();
  const { data } = useQuery({
    queryKey: ["bpin-pending-summary", daneCode],
    queryFn: () => fetchBpinPendingSummary(daneCode as string),
    enabled: Boolean(daneCode),
    refetchInterval: 600_000,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return false;
      }
      return count < 1;
    },
  });

  if (!data || data.count === 0) return null;

  const firstProject = data.projects[0];
  const firstName = firstProject?.name;
  const hasMore = data.count > 1;
  // #571: con un solo radicado sin BPIN, el CTA abre ese expediente en la
  // pestana Radicar (donde vive `BpinSection`). Antes apuntaba siempre a
  // `/expedientes`, y desde la lista el clic no hacia nada. Con varios,
  // la lista sigue siendo el destino: no hay un unico expediente que abrir.
  const ctaTo =
    data.count === 1 && firstProject
      ? `/expedientes/${firstProject.id}?tab=radicar`
      : "/expedientes";

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="bpin-pending-banner"
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 sm:px-6"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-1">
        <span aria-hidden="true">⚠️</span>
        <span className="flex-1">
          {data.count === 1 ? (
            <>
              Tu expediente <strong>{firstName}</strong> está radicado pero todavía no tiene BPIN
              registrado.
            </>
          ) : (
            <>
              Tienes <strong>{data.count} expedientes radicados</strong> sin BPIN registrado
              {hasMore && firstName ? ` (${firstName} y otros)` : ""}.
            </>
          )}{" "}
          Sin BPIN no podemos hacer seguimiento al primer giro en SIIF Nación.
        </span>
        <Link
          to={ctaTo}
          className="rounded-md bg-amber-200 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-300"
        >
          Completar BPIN
        </Link>
      </div>
    </div>
  );
}

function CloseIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
